import { useToast } from '@/hooks/use-toast';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  createLunchMenu,
  deleteLunchMenu,
  getLunchMenus,
  getLunchRequests,
  updateLunchMenu,
} from '../services/manage-lunch.service';
import {
  GetLunchMenusParams,
  GetLunchRequestsParams,
  LunchMenuInsertInput,
  LunchMenuUpdateInput,
} from '../types/manage-lunch.types';

const invalidateMenuQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-lunch-menus'] });

export const useGetLunchMenus = (params: GetLunchMenusParams) => {
  return useGlobalQuery({
    queryKey: ['hr-lunch-menus', params],
    queryFn: () => getLunchMenus(params),
    staleTime: 60 * 1000,
  });
};

export const useGetLunchRequests = (params: GetLunchRequestsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-manage-lunch-requests', params],
    queryFn: () => getLunchRequests(params),
    staleTime: 60 * 1000,
  });
};

export const useCreateLunchMenu = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: LunchMenuInsertInput) => createLunchMenu(input),
    onSuccess: () => {
      invalidateMenuQueries(queryClient);
      toast({ title: t('MENU_CREATED') });
    },
  });
};

export const useUpdateLunchMenu = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: LunchMenuUpdateInput }) =>
      updateLunchMenu(params),
    onSuccess: () => invalidateMenuQueries(queryClient),
  });
};

export const useDeleteLunchMenu = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteLunchMenu(itemId),
    onSuccess: () => {
      invalidateMenuQueries(queryClient);
      toast({ title: t('MENU_DELETED') });
    },
  });
};
